import React, {useEffect, useMemo, useState} from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    LinearProgress,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import {Drawer} from "@mui/material"
import ApiError from "../../api/ApiError"
import ErrorMessage from "../error/ErrorMessage"
import {useProjectData} from "../../hooks/useProjectData"
import {TransformRequests} from "../../api/requests/TransformRequests"
import TransformJson from "../../api/json/TransformJson"
import TransformJobJson from "../../api/json/TransformJobJson"
import TransformActionsMenu from "./TransformActionsMenu"

interface TransformsDrawerProps {
    open: boolean
    onClose: () => void
    onNew: () => void
    onEdit: (transformId: string) => void
    /** When the user starts a new job elsewhere, push it into the drawer's local state too. */
    optimisticTransformJob?: {
        key: number
        job: TransformJobJson
        transformId: string
        transformName: string
    }
}

// Show the search column when there are enough transforms that scanning gets painful.
const SEARCH_THRESHOLD = 8
const POLL_INTERVAL_MS = 2000

function TransformsDrawer({open, onClose, onNew, onEdit, optimisticTransformJob}: TransformsDrawerProps) {
    const {id: projectId, project} = useProjectData()
    const [transforms, setTransforms] = useState<TransformJson[]>()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<ApiError>()
    const [query, setQuery] = useState("")

    const load = (silent: boolean = false) => {
        if (!projectId) return
        if (!silent) setLoading(true)
        TransformRequests.list(projectId, 0)
            .then(page => {
                setTransforms(page.content)
                setError(undefined)
            })
            .catch(err => {
                if (!silent) setError(err)
            })
            .finally(() => {
                if (!silent) setLoading(false)
            })
    }

    useEffect(() => {
        if (!open) return
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projectId])

    // Poll for live job progress while any transform has an active job.
    const anyActive = transforms?.some(t => t.hasActiveJob) ?? false
    useEffect(() => {
        if (!open || !anyActive) return
        const interval = setInterval(() => load(true), POLL_INTERVAL_MS)
        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, anyActive, projectId])

    // When a job is started from elsewhere (TransformEditModal inline form), push it into
    // local state so the drawer immediately shows the "Running" chip + progress without
    // waiting for a reload. Polling will take over from there.
    useEffect(() => {
        if (!optimisticTransformJob) return
        const {job, transformId} = optimisticTransformJob
        setTransforms(prev => prev?.map(t =>
            t.id === transformId
                ? {
                    ...t,
                    hasActiveJob: true,
                    transformJobs: t.transformJobs.some(j => j.id === job.id)
                        ? t.transformJobs
                        : [job, ...t.transformJobs],
                }
                : t
        ))
        // Also trigger a silent reload in case the optimistic push happened before
        // `transforms` was loaded.
        load(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optimisticTransformJob?.key])

    const handleRenamed = (renamed: TransformJson) => {
        setTransforms(prev => prev?.map(t => t.id === renamed.id ? renamed : t))
    }

    const handleCopied = (copy: TransformJson) => {
        setTransforms(prev => prev ? [copy, ...prev] : [copy])
    }

    const handleDeleted = (deletedId: string) => {
        setTransforms(prev => prev?.filter(t => t.id !== deletedId))
    }

    const filtered = useMemo(() => {
        if (!transforms) return undefined
        const q = query.trim().toLowerCase()
        if (!q) return transforms
        return transforms.filter(t =>
            t.name.toLowerCase().includes(q)
            || (t.userPrompt ?? "").toLowerCase().includes(q)
            || (t.systemPrompt ?? "").toLowerCase().includes(q)
        )
    }, [transforms, query])

    const showSearch = (transforms?.length ?? 0) >= SEARCH_THRESHOLD

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{width: 520, display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Stack direction="row"
                       alignItems="center"
                       justifyContent="space-between"
                       sx={{px: 3, py: 2}}>
                    <Typography variant="h3" fontSize={18} fontWeight={500}>Transforms</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon/>
                    </IconButton>
                </Stack>
                <Divider/>

                <Box sx={{flex: '1 1 auto', overflowY: 'auto', p: 3}}>
                    <Button variant="contained"
                            fullWidth
                            startIcon={<AddIcon/>}
                            onClick={onNew}
                            sx={{mb: 2}}>
                        New transform
                    </Button>

                    {showSearch &&
                        <TextField size="small"
                                   fullWidth
                                   placeholder="Search transforms…"
                                   value={query}
                                   onChange={(e) => setQuery(e.target.value)}
                                   InputProps={{
                                       startAdornment: <SearchIcon fontSize="small" sx={{mr: 1, color: 'text.secondary'}}/>,
                                   }}
                                   sx={{mb: 2}}/>
                    }

                    {error && <ErrorMessage error={error}/>}
                    {loading && !transforms &&
                        <Stack alignItems="center" sx={{mt: 4}}><CircularProgress size={24}/></Stack>
                    }

                    {transforms && transforms.length === 0 &&
                        <Stack spacing={1} alignItems="center" sx={{mt: 4, textAlign: 'center'}}>
                            <Typography variant="body1" color="text.secondary">
                                No transforms yet.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Create one to enrich rows in bulk.
                            </Typography>
                        </Stack>
                    }

                    {filtered && transforms && transforms.length > 0 && filtered.length === 0 &&
                        <Typography variant="body2" color="text.secondary" sx={{mt: 2, textAlign: 'center'}}>
                            No transforms match "{query}".
                        </Typography>
                    }

                    {filtered && filtered.length > 0 &&
                        <Stack spacing={1.5}>
                            {filtered.map(transform => {
                                // Find the latest active job, if any, to show inline progress.
                                const activeJob = transform.transformJobs.find(j => j.active)
                                const jobProgress = activeJob && activeJob.totalTasks && activeJob.totalTasks > 0
                                    ? ((activeJob.succeededCount + activeJob.failedCount) / activeJob.totalTasks) * 100
                                    : null
                                return (
                                    <Box key={transform.id}
                                         onClick={() => onEdit(transform.id)}
                                         sx={{
                                             border: '1px solid #e0e0e0',
                                             borderRadius: 1,
                                             backgroundColor: '#fff',
                                             p: 2,
                                             cursor: 'pointer',
                                             '&:hover': {borderColor: '#bbb'},
                                         }}>
                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                            <Box sx={{minWidth: 0, flex: 1}}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="subtitle2" noWrap>
                                                        {transform.name}
                                                    </Typography>
                                                    {transform.hasActiveJob &&
                                                        <Chip label="Running" size="small" color="warning" variant="outlined"/>
                                                    }
                                                </Stack>
                                                <Typography variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 0.5,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}>
                                                    {transform.userPrompt || transform.systemPrompt || "(empty)"}
                                                </Typography>
                                                {activeJob && activeJob.totalTasks != null &&
                                                    <Box sx={{mt: 1}}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {activeJob.succeededCount + activeJob.failedCount} / {activeJob.totalTasks} rows
                                                        </Typography>
                                                        <LinearProgress variant="determinate"
                                                                        value={jobProgress ?? 0}
                                                                        sx={{mt: 0.5, height: 3, borderRadius: 2}}/>
                                                    </Box>
                                                }
                                            </Box>
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                {project &&
                                                    <TransformActionsMenu project={project}
                                                                          transform={transform}
                                                                          didRename={handleRenamed}
                                                                          didCopy={handleCopied}
                                                                          didDelete={() => handleDeleted(transform.id)}/>
                                                }
                                            </Box>
                                        </Stack>
                                    </Box>
                                )
                            })}
                        </Stack>
                    }
                </Box>
            </Box>
        </Drawer>
    )
}

export default TransformsDrawer
